import { useEffect, useMemo, useRef, useState } from "react";
import {
  AreaSeries,
  createChart,
  type AreaData,
  type IChartApi,
  type Time,
} from "lightweight-charts";
import {
  fetchRiskFlowStockChart,
  type RiskFlowStockChartData,
  type RiskFlowStockChartPoint,
} from "../../lib/riskflow-stock-chart-api";
import type { RiskFlowPostSurface } from "./RiskFlowPostCard";

interface RiskFlowStockChartFrameProps {
  ticker: string;
  href: string;
  label: string;
  surface: RiskFlowPostSurface;
}

export function RiskFlowStockChartFrame({
  ticker,
  label,
  surface,
}: RiskFlowStockChartFrameProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const [data, setData] = useState<RiskFlowStockChartData | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "empty">(
    "loading",
  );

  useEffect(() => {
    let isActive = true;
    setStatus("loading");
    setData(null);
    fetchRiskFlowStockChart(ticker)
      .then((nextData) => {
        if (!isActive) return;
        setData(nextData);
        setStatus(hasChartPoints(nextData) ? "ready" : "empty");
      })
      .catch(() => {
        if (isActive) setStatus("empty");
      });
    return () => {
      isActive = false;
    };
  }, [ticker]);

  const points = useMemo(() => selectChartPoints(data), [data]);

  useEffect(() => {
    if (!containerRef.current || points.length < 2) return;

    const chart = createChart(containerRef.current, {
      autoSize: true,
      layout: {
        background: { color: "transparent" },
        textColor: "rgba(240, 234, 214, 0.58)",
        fontFamily: "var(--font-data, ui-monospace, monospace)",
        fontSize: 10,
      },
      grid: {
        vertLines: { color: "rgba(199, 159, 74, 0.035)" },
        horzLines: { color: "rgba(199, 159, 74, 0.05)" },
      },
      rightPriceScale: {
        borderColor: "rgba(199, 159, 74, 0.12)",
        scaleMargins: { top: 0.16, bottom: 0.18 },
      },
      timeScale: {
        borderColor: "rgba(199, 159, 74, 0.12)",
        timeVisible: true,
        secondsVisible: false,
      },
      crosshair: { mode: 1 },
    });
    chartRef.current = chart;

    const isPositive = (data?.change ?? inferPointChange(points)) >= 0;
    const lineColor = isPositive ? "#22d6a0" : "#ff4d57";
    const series = chart.addSeries(AreaSeries, {
      lineColor,
      topColor: isPositive
        ? "rgba(34, 214, 160, 0.24)"
        : "rgba(255, 77, 87, 0.22)",
      bottomColor: "rgba(5, 4, 2, 0.02)",
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: true,
      crosshairMarkerVisible: true,
    });
    series.setData(toAreaData(points));
    chart.timeScale().fitContent();

    return () => {
      chart.remove();
      chartRef.current = null;
    };
  }, [data?.change, points]);

  return (
    <div
      className={`relative overflow-hidden ${chartHeightClass(surface)}`}
      data-riskflow-stock-chart={ticker}
      data-weekly-walkthrough-target="riskflow-stock-context"
      aria-label={label}
    >
      <div ref={containerRef} className="h-full w-full" />
      {status !== "ready" && (
        <div className="absolute inset-0 flex items-center justify-center bg-[var(--fintheon-bg)] text-[10px] uppercase tracking-[0.16em] text-[var(--fintheon-text)]/35">
          {status === "loading"
            ? `Loading ${ticker}`
            : `${ticker} chart unavailable`}
        </div>
      )}
      <div className="pointer-events-none absolute left-3 top-2 flex items-baseline gap-2">
        <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--fintheon-text)]/80">
          ${ticker}
        </span>
        <span
          className={`font-mono text-[10px] tabular-nums ${changeClass(data?.change)}`}
        >
          {formatSignedMove(data)}
        </span>
      </div>
    </div>
  );
}

function chartHeightClass(surface: RiskFlowPostSurface): string {
  return surface === "mini" ? "h-40" : "h-72";
}

function selectChartPoints(
  data: RiskFlowStockChartData | null,
): RiskFlowStockChartPoint[] {
  if (!data) return [];
  if (data.sparkline5d.length >= 2) return data.sparkline5d;
  if (data.sparkline1d.length >= 2) return data.sparkline1d;
  return data.sparkline;
}

function hasChartPoints(data: RiskFlowStockChartData | null): boolean {
  return selectChartPoints(data).length >= 2;
}

function toAreaData(points: RiskFlowStockChartPoint[]): AreaData[] {
  return points.map((point) => ({
    time: normalizeChartTime(point.time),
    value: point.close,
  }));
}

function normalizeChartTime(time: number): Time {
  return (time > 1e12 ? Math.floor(time / 1000) : Math.floor(time)) as Time;
}

function inferPointChange(points: RiskFlowStockChartPoint[]): number {
  const first = points[0]?.close ?? 0;
  const last = points.at(-1)?.close ?? first;
  return last - first;
}

function formatSignedMove(data: RiskFlowStockChartData | null): string {
  if (!data?.changePercent && !data?.change) return "live chart";
  const change = data.change == null ? "" : signed(data.change);
  const pct =
    data.changePercent == null ? "" : `${signed(data.changePercent)}%`;
  return [change, pct].filter(Boolean).join(" · ");
}

function signed(value: number): string {
  return `${value >= 0 ? "+" : ""}${value.toFixed(2)}`;
}

function changeClass(value: number | null | undefined): string {
  if (value == null) return "text-[var(--fintheon-text)]/35";
  return value >= 0 ? "text-[var(--fintheon-bullish)]" : "text-[#ff4d57]";
}
